import type {
  ContaPagarApi,
  ContaPagarStatusTipo,
  ContaPagarView,
  ContasPagarApiResponse,
  ContasPagarView,
} from "../types/contas-pagar.types";
import {
  diferencaDiasCivisFinanceiro,
  formatarDataCivilFinanceiro,
  formatarDataMovimentacaoFinanceiro,
  hojeYmdFinanceiro,
  ymdCivilFinanceiro,
} from "./formatar-data-financeiro";
import { toNumber } from "./map-dre-api-to-view";

const CATEGORIA_LABELS: Record<string, string> = {
  MARKETING: "Marketing",
  FRETE: "Frete",
  INFRAESTRUTURA: "Infraestrutura",
  SERVICOS: "Serviços",
  EVENTOS: "Eventos",
  FORNECEDORES: "Fornecedores",
  IMPOSTOS: "Impostos",
  OPERACIONAL: "Operacional",
  OUTROS: "Outros",
};

const CONTEXTO_LABELS: Record<string, string> = {
  LOJA: "Loja",
  EVENTO: "Evento",
  OPERACIONAL: "Operacional",
  ADMINISTRATIVO: "Administrativo",
  OUTROS: "Outros",
};

const NATUREZA_LABELS: Record<string, string> = {
  FIXA: "Fixa",
  VARIAVEL: "Variável",
};

const STATUS_LABELS: Record<string, string> = {
  PENDENTE: "Pendente",
  PAGO: "Pago",
  VENCIDO: "Vencido",
  CANCELADO: "Cancelado",
};

function listaItens(api: ContasPagarApiResponse): ContaPagarApi[] {
  return api.data ?? api.items ?? api.titulos ?? api.registros ?? [];
}

function vencimentoRaw(row: ContaPagarApi): string | null {
  const raw = row.dataVencimento ?? row.vencimentoEm ?? null;
  return raw != null && String(raw).trim() ? String(raw).trim() : null;
}

function statusParaTipo(status: string): ContaPagarStatusTipo {
  const s = status.toUpperCase();
  if (s === "PAGO") return "pago";
  if (s === "VENCIDO") return "vencido";
  if (s === "CANCELADO") return "cancelado";
  if (s === "PENDENTE") return "pendente";
  return "neutro";
}

function calcularVencido(
  statusRaw: string,
  vencimentoIso: string | null,
  aberto: number,
): boolean {
  if (!vencimentoIso || aberto <= 0) return false;
  const s = statusRaw.toUpperCase();
  const vencYmd = ymdCivilFinanceiro(vencimentoIso);
  const hojeYmd = hojeYmdFinanceiro();
  if (!vencYmd) return false;
  if (s === "VENCIDO") return true;
  if (s === "PENDENTE" && vencYmd < hojeYmd) return true;
  return false;
}

function calcularDiasAtraso(vencido: boolean, vencimentoIso: string | null): number {
  if (!vencido || !vencimentoIso) return 0;
  const vencYmd = ymdCivilFinanceiro(vencimentoIso);
  const hojeYmd = hojeYmdFinanceiro();
  if (!vencYmd) return 0;
  return Math.max(0, diferencaDiasCivisFinanceiro(vencYmd, hojeYmd));
}

function mapItem(row: ContaPagarApi): ContaPagarView {
  const id = String(row.id ?? "");
  const statusRaw = String(row.status ?? "").trim() || "PENDENTE";
  const vencimentoIso = vencimentoRaw(row);

  const valorTotal = toNumber(row.valorTotal);
  let valorPago = toNumber(row.valorPago);
  let valorAberto = toNumber(row.valorAberto);

  if (valorAberto === 0 && valorTotal > 0) {
    const tipo = statusParaTipo(statusRaw);
    if (tipo === "pago") {
      valorPago = valorPago || valorTotal;
      valorAberto = 0;
    } else if (tipo === "cancelado") {
      valorPago = 0;
      valorAberto = 0;
    } else {
      valorAberto =
        valorTotal - valorPago > 0 ? valorTotal - valorPago : valorTotal;
    }
  }

  const vencido = calcularVencido(statusRaw, vencimentoIso, valorAberto);
  const diasAtraso = calcularDiasAtraso(vencido, vencimentoIso);

  const ccNome = row.centroCustoNome?.trim();
  const semCentro = !row.centroCustoId || !ccNome;

  const cat = String(row.categoria ?? "").trim() || "OUTROS";
  const ctx = String(row.contexto ?? "").trim() || "OUTROS";
  const nat = String(row.naturezaDespesa ?? "VARIAVEL").trim() || "VARIAVEL";

  const fornecedorNome =
    row.fornecedorNome?.trim() || "Fornecedor não informado";

  const baseImposto =
    row.baseCalculoImposto != null && String(row.baseCalculoImposto).trim()
      ? toNumber(row.baseCalculoImposto)
      : null;
  const aliImposto =
    row.aliquotaImposto != null && String(row.aliquotaImposto).trim()
      ? toNumber(row.aliquotaImposto)
      : null;

  return {
    id,
    numero: row.numero != null ? String(row.numero) : "—",
    fornecedorId: row.fornecedorId?.trim() ? row.fornecedorId.trim() : null,
    fornecedorNome,
    fornecedorDocumento: row.fornecedorDocumento?.trim()
      ? row.fornecedorDocumento.trim()
      : null,
    centroCustoId: row.centroCustoId?.trim() ? row.centroCustoId.trim() : null,
    centroCustoNome: semCentro ? "Sem centro" : ccNome!,
    semCentro,
    categoria: cat,
    categoriaLabel: CATEGORIA_LABELS[cat] ?? cat.replace(/_/g, " "),
    contexto: ctx,
    contextoLabel: CONTEXTO_LABELS[ctx] ?? ctx.replace(/_/g, " "),
    naturezaDespesa: nat,
    naturezaLabel: NATUREZA_LABELS[nat] ?? nat,
    eventoId:
      row.eventoId != null && !Number.isNaN(Number(row.eventoId))
        ? Number(row.eventoId)
        : null,
    descricao: row.descricao?.trim() || "—",
    observacao: row.observacao?.trim() ? row.observacao.trim() : null,
    baseCalculoImposto: baseImposto,
    aliquotaImposto: aliImposto,
    valorTotal,
    valorPago,
    valorAberto,
    dataVencimento: vencimentoIso,
    dataVencimentoLabel: formatarDataCivilFinanceiro(vencimentoIso),
    competenciaEm: row.competenciaEm?.trim() ?? null,
    pagoEm: row.pagoEm?.trim() ?? null,
    pagoEmLabel: formatarDataMovimentacaoFinanceiro(row.pagoEm, {
      comHora: false,
    }),
    status: statusRaw,
    statusLabel: STATUS_LABELS[statusRaw.toUpperCase()] ?? statusRaw,
    statusTipo: statusParaTipo(statusRaw),
    diasAtraso,
    vencido,
  };
}

function resumoSomentePagina(itens: ContaPagarView[]): ContasPagarView["resumo"] {
  const hojeYmd = hojeYmdFinanceiro();

  let totalAberto = 0;
  let totalVencido = 0;
  let totalVenceHoje = 0;
  let totalPago = 0;
  let totalProximos30Dias = 0;
  let quantidadeAberto = 0;
  let quantidadeVencido = 0;

  for (const it of itens) {
    if (it.valorAberto > 0) {
      totalAberto += it.valorAberto;
      quantidadeAberto += 1;
    }
    if (it.statusTipo === "pago" && it.valorPago > 0) totalPago += it.valorPago;

    if (it.vencido && it.valorAberto > 0) {
      totalVencido += it.valorAberto;
      quantidadeVencido += 1;
    }

    const venYmd = ymdCivilFinanceiro(it.dataVencimento);
    if (venYmd && it.valorAberto > 0) {
      if (venYmd === hojeYmd) totalVenceHoje += it.valorAberto;
      const dias = diferencaDiasCivisFinanceiro(hojeYmd, venYmd);
      if (dias >= 0 && dias <= 30) totalProximos30Dias += it.valorAberto;
    }
  }

  return {
    totalAberto,
    totalVencido,
    totalVenceHoje,
    totalPago,
    totalProximos30Dias,
    quantidadeAberto,
    quantidadeVencido,
  };
}

function mergeResumo(
  api: ContasPagarApiResponse,
  itens: ContaPagarView[],
): ContasPagarView["resumo"] {
  const r = api.resumo;
  if (r && typeof r === "object") {
    return {
      totalAberto: toNumber(r.totalAberto),
      totalVencido: toNumber(r.totalVencido),
      totalVenceHoje: toNumber(r.totalVenceHoje),
      totalPago: toNumber(r.totalPago),
      totalProximos30Dias: toNumber(r.totalProximos30Dias),
      quantidadeAberto: Number(r.quantidadeAberto ?? 0),
      quantidadeVencido: Number(r.quantidadeVencido ?? 0),
    };
  }
  return resumoSomentePagina(itens);
}

export function mapContasPagarApiToView(
  api: ContasPagarApiResponse | null | undefined,
): ContasPagarView | null {
  if (!api) return null;

  const rawList = listaItens(api);
  const itens = rawList.map(mapItem);

  const total = typeof api.total === "number" ? api.total : itens.length;
  const page = typeof api.page === "number" ? api.page : 1;
  const limit = typeof api.limit === "number" ? api.limit : itens.length || 20;
  const totalPages =
    typeof api.totalPages === "number"
      ? api.totalPages
      : Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return {
    itens,
    total,
    page,
    limit,
    totalPages,
    resumo: mergeResumo(api, itens),
  };
}
